import { createFileRoute } from "@tanstack/react-router";
import { socialMeta } from "@/lib/social-meta";
import { ORG_WEBSITE_JSONLD } from "@/lib/structured-data";
import { Link } from "@tanstack/react-router";
import { ArrowRight, Check, Clock3, Crosshair, MapPin, QrCode, Route as RouteIcon, ShieldCheck } from "lucide-react";
import { PublicFooter, PublicHeader } from "@/components/landing/MissionReadyPublicSite";
import { PatrolTrailDemo } from "@/components/landing/PatrolTrailDemo";

export const Route = createFileRoute("/patrol")({
  component: PatrolPage,
  head: () => ({
    meta: [
      { title: "Patrol Tracking & Guard Tour Software — MissionReady" },
      { name: "description", content: "GPS patrol tracking, signed QR checkpoint scanning, patrol replay, and anomaly detection for security guard tours. Prove every patrol — to clients and regulators." },
      ...socialMeta({
        title: "Patrol Tracking & Guard Tour Software — MissionReady",
        description: "GPS patrol tracking, signed QR checkpoint scanning, and patrol replay for security guard tours.",
        url: "https://missionready.dev/patrol",
      }),
    ],
    links: [{ rel: "canonical", href: "https://missionready.dev/patrol" }],
    scripts: [
      ...ORG_WEBSITE_JSONLD,{
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        name: "MissionReady Patrol",
        applicationCategory: "BusinessApplication",
        operatingSystem: "Web, iOS, Android",
        description: "GPS patrol tracking, signed QR checkpoint scanning, patrol replay, and anomaly detection for security guard tours.",
        url: "https://missionready.dev/patrol",
        brand: { "@type": "Brand", name: "MissionReady" },
        offers: { "@type": "Offer", price: "0", priceCurrency: "USD", url: "https://missionready.dev/pricing" },
        featureList: [
          "Live GPS patrol tracking",
          "Signed QR checkpoint scanning",
          "Patrol replay and heatmaps",
          "Anomaly detection",
          "Reusable patrol routes",
          "Client-ready PDF tour reports",
        ],
      }),
    }],
  }),
});

const FEATURES = [
  [Crosshair, "Live patrol visibility", "Follow active officers and patrol progress from the operations center."],
  [MapPin, "GPS tracking", "Capture location updates throughout the patrol with accountable timestamps."],
  [RouteIcon, "Breadcrumb history", "Replay the movement path and review how site coverage developed."],
  [QrCode, "Checkpoint verification", "Connect signed QR scans to required points on the assigned route. Print checkpoint codes at any size and pinch-zoom them on screen for awkward mounting spots."],
  [QrCode, "Works without signal", "Scans, incident reports with photos and video, and voice memos are saved on the device and sync themselves when coverage returns — stamped with the time they happened."],
  [ShieldCheck, "Officer accountability", "Pair identity, movement, scans, and status in one operational record."],
  [Clock3, "Timestamped activity", "Preserve the sequence of activity for supervisors and client reporting."],
] as const;

function PatrolPage() {
  return (
    <div className="mr-public">
      <PublicHeader />
      <main>
        <section className="mr-product-hero"><div className="mr-grid-bg" aria-hidden="true" /><div className="mr-shell"><span className="mr-live-chip"><i /> MISSIONREADY LIVE PATROL</span><h1>PROVE EVERY PATROL.<br /><em>VERIFY EVERY STEP.</em></h1><p>Live GPS visibility, breadcrumb history, and checkpoint verification built for accountable field operations.</p><div className="mr-process"><span>TRACK</span><ArrowRight /><span>VERIFY</span><ArrowRight /><span>PROVE</span></div></div></section>
        <section className="mr-story-section is-detailed"><div className="mr-shell mr-story-grid"><div className="mr-story-copy"><span className="mr-interactive-label"><i /> LIVE BREADCRUMB TRAIL</span><h2>SEE THE OPERATION.<br /><em>LIVE.</em></h2><p>MissionReady doesn't just tell you what happened. It creates the operational trail. Pause, replay, and inspect timestamped route points and verified checkpoints.</p></div><PatrolTrailDemo detailed /></div></section>
        <section className="mr-capabilities"><div className="mr-shell"><div className="mr-section-heading"><h2>Patrol tracking and checkpoint features</h2></div><div className="mr-capability-grid">{FEATURES.map(([Icon, title, copy]) => <article key={title}><Icon /><h3>{title}</h3><p>{copy}</p></article>)}</div><div className="mr-product-cta"><h2>RUN THE PATROL.<br /><em>KEEP THE PROOF.</em></h2><p>Put live visibility and verifiable activity history into every shift.</p><Link to="/signup" className="mr-button">START FREE <ArrowRight /></Link><span><Check /> 30-day trial · No credit card</span></div></div></section>
      </main>
      <PublicFooter />
    </div>
  );
}
